import React from 'react';
import PropTypes from 'prop-types';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Calendar, AlertTriangle, Clock, Bell, Circle, ArrowRight } from 'lucide-react';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'project':
      return <Calendar className="text-blue-500" size={20} />;
    case 'risk':
      return <AlertTriangle className="text-red-500" size={20} />;
    case 'reminder':
      return <Clock className="text-green-500" size={20} />;
    default:
      return <Bell className="text-gray-500" size={20} />;
  }
};

const getNotificationContent = (notification) => {
  
  switch (notification.type) {
    case 'project':
      return {
        title: notification.projectName,
        subtitle: `Due: ${new Date(notification.endDate).toLocaleDateString('en-GB')}`,
        chip: 'Project',
        chipColor: 'primary'
      };
    case 'risk':
      return {
        title: notification.description,
        subtitle: `Impact: ${notification.impact} | Probability: ${notification.probability}`,
        chip: 'Risk Alert',
        chipColor: 'error'
      };
    case 'reminder':
      return {
        title: notification.title,
        subtitle: `Date: ${new Date(notification.datetime).toLocaleString('en-GB')}`,
        chip: 'Reminder',
        chipColor: 'success'
      };
    case 'news':
      return {
        title: notification.title,
        subtitle: `Posted: ${new Date(notification.createdAt).toLocaleDateString('en-GB')}`,
        chip: 'News',
        chipColor: 'info'
      };
    case 'event':
      return {
        title: notification.title,
        subtitle: `Event Date: ${new Date(notification.date).toLocaleDateString('en-GB')}`,
        chip: 'Event',
        chipColor: 'warning'
      };
    default:
      return {
        title: '',
        subtitle: '',
        chip: '',
        chipColor: 'default'
      };
  }
};

const NotificationItem = ({ notification, onMarkAsRead }) => {
  const content = getNotificationContent(notification);

  return (
    <ListItem
      sx={{
        py: 2,
        px: 3,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>
        {getNotificationIcon(notification.type)}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="subtitle1" component="span">
              {content.title}
            </Typography>
            <Chip
              label={content.chip}
              color={content.chipColor}
              size="small"
              sx={{ height: 20 }}
            />
          </Box>
        }
        secondary={
          <Typography variant="body2" color="text.secondary">
            {content.subtitle}
          </Typography>
        }
      />
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title={notification.IsNotified ? "Read" : "Unread"}>
          <Circle
            size={12}
            className={notification.IsNotified ? 'text-gray-300' : 'text-blue-500 fill-current'}
          />
        </Tooltip>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => onMarkAsRead(notification)}
            sx={{ ml: 1 }}
          >
            <ArrowRight size={20} />
          </IconButton>
        </Tooltip>
      </Box>
    </ListItem>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    IsNotified: PropTypes.bool.isRequired,
    type: PropTypes.oneOf(['project', 'risk', 'reminder', 'news', 'event']).isRequired,
    title: PropTypes.string,
    projectName: PropTypes.string,
    description: PropTypes.string,
    impact: PropTypes.string,
    probability: PropTypes.string,
    endDate: PropTypes.shape({
      $date: PropTypes.string
    }),
    datetime: PropTypes.shape({
      $date: PropTypes.string
    }),
    createdAt: PropTypes.string,
    date: PropTypes.string
  }).isRequired,
  onMarkAsRead: PropTypes.func.isRequired
};

export default NotificationItem;